import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Col, Row, Button, Alert, Spinner, Tabs, Tab, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import API from "../../Utility";
import { ProjectContext } from "../../ProjectContext";
import AssignWorkflowUsersFormModal from './AssignWorkflowUsersFormModal';
import ViewAssignedUsersTasksModal from './ViewAssignedUsersTasksModal';
import Moment from "react-moment";
import moment from 'moment';
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import ErrorModal from '../../ErrorModal/ErrorModal';
import MultiSelectTypeahead from "../../MultiSelectTypeahead";
import { FormGroup, Form, FormControl, InputGroup } from "react-bootstrap";
import User from "./User";
import ImportWorkflowUsersFormModal from '../WorkflowUsers/ImportWorkflowUsersFormModal';
import CircularProgress from '@material-ui/core/CircularProgress';

// Styles
import "./WorkflowUsers.scss";
import { FaEdit, FaPlus, FaTimes, FaRegTrashAlt, FaCheck, FaCheckSquare, FaRegCheckSquare, FaDownload } from "react-icons/fa";
import { LuRefreshCcw } from 'react-icons/lu';

export default function WorkflowUsers({ children, workflow, refreshUsers, setRefreshUsers, refreshTasks, setRefreshTasks, setRefreshWorkflow, ...routeProps }) {
  let { workflowId } = useParams();
  const location = useLocation();
  let stateWorkflow = location && location.state ? location.state.workflow : {};
  const [workflowUsers, setWorkflowUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { project } = useContext(ProjectContext);
  const [showAssignUsersFormModal, setShowAssignUsersFormModal] = useState();
  const [assignUserMode, setAssignUserMode] = useState('create');
  const [editUser, setEditUser] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [showConfirmUnassign, setShowConfirmUnassign] = useState(false);
  const [assignmentToView, setAssignmentToView] = useState(null);
  const [showConfirmView, setShowConfirmView] = useState(false);
  const [showDownloadDataError, setShowDownloadDataError] = useState(false);

  const [statusFilter, setStatusFilter] = useState("AllIncomplete");
  const [participantFilter, setParticipantFilter] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);

  const minSearchLength = 3;
  let pageSize = 50;
  let loadIncrement = 40;
  // let pageSize = 10;
  // let loadIncrement = 8;
  const [itemsToLoadPerPage, setItemsToLoadPerPage] = useState(loadIncrement);
  const [itemsLoaded, setItemsLoaded] = useState([]);
  const [page, setPage] = useState(1);
  const [lastTaskId, setLastTaskId] = useState(null);
  const [lastUserId, setLastUserId] = useState(null);
  const [loadMore, setLoadMore] = useState(true);
  const [moreItemsToLoad, setMoreItemsToLoad] = useState(true);
  const [isDownloadingData, setIsDownloadingData] = useState(false);

  const [showDisableTooltip, setShowDisableTooltip] = useState(false);

  const [showImportWorkflowUsersFormModal, setShowImportWorkflowUsersFormModal] = useState();

  async function getWorkflowUsersData(query) {
    try {
      setIsLoading(true);
      let apiWorkflow = await API.get({
        url: query.url,
      });
      setIsLoading(false);
      return apiWorkflow;
    } catch (error) {
      console.log(error);
    }
  }
  async function initialiseWorkflowUsers() {
    let url = `/admin/${project?._id}/workflows/${workflowId}/users`;
    if (page) {
      url += `?page=${page}`;
    }
    if (pageSize) {
      url += page ? `&pageSize=${pageSize}` : `?pageSize=${pageSize}`;
    }
    if (statusFilter) {
      url += page || pageSize ? `&statusMatch=${statusFilter}` : `?statusMatch=${statusFilter}`;
    }
    if (participantFilter) {
      url += page || pageSize || statusFilter ? `&participantsMatch=${participantFilter}` : `?participantsMatch=${participantFilter}`;
    }
    if (assigneeFilter) {
      url += page || pageSize || statusFilter || participantFilter ? `&assigneesMatch=${assigneeFilter}` : `?assigneesMatch=${assigneeFilter}`;
    }
    let apiWorkflowUsers = await getWorkflowUsersData({
      url: url,
    });

    // If any of the filters have changed, or the users have been refreshed, reset the workflow users
    // Else add the new API users to the existing workflow users
    if (refreshUsers === true || (statusFilter !== previousValues?.statusFilter) || (participantFilter !== previousValues?.participantFilter && (participantFilter === "" || participantFilter?.length > 0)) || (assigneeFilter !== previousValues?.assigneeFilter && (assigneeFilter === "" || assigneeFilter?.length > 0))) {
      setWorkflowUsers(apiWorkflowUsers.filteredResults);
    } else {
      setWorkflowUsers([ ...workflowUsers, ...apiWorkflowUsers.filteredResults ]);
    }
    setRefreshUsers(false);
    setRefreshTasks(false);
    setLoadMore(false);
    // The last API response indicates that there is no more data to load
    if (!apiWorkflowUsers?.filteredResults?.[0] || apiWorkflowUsers?.filteredResults?.length < pageSize) {
      setMoreItemsToLoad(false);
    }
  }

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const previousValues = usePrevious({ project, refreshUsers, statusFilter, participantFilter, assigneeFilter});

  const useEffectWithPreviousCheck = (effectHook, dependencies, dependencyNames = [], logChangedDeps = false) => {
    const previousDeps = usePrevious(dependencies, []);

    const changedDeps = dependencies.reduce((accum, dependency, index) => {
      if (dependency !== previousDeps?.[index]) {
        const keyName = dependencyNames[index] || index;
        return {
          ...accum,
          [keyName]: {
            before: previousDeps?.[index],
            after: dependency
          }
        };
      }

      return accum;
    }, {});

    if (Object?.keys?.(changedDeps)?.length && logChangedDeps) {
      console.log('[use-effect-debugger] ', changedDeps);
    }

    useEffect(() => effectHook(changedDeps), dependencies);
  };

  useEffectWithPreviousCheck((changes) => {
    // If none of the filters have changed, and the users have not been refreshed, load more items
    if (project?._id && loadMore && moreItemsToLoad) {
      // console.log('Loading more items...');
      setIsLoading(true);
      initialiseWorkflowUsers();
    }
  }, [project, statusFilter, participantFilter, assigneeFilter, loadMore, moreItemsToLoad], ['project', 'statusFilter', 'participantFilter', 'assigneeFilter', 'loadMore', 'moreItemsToLoad'], false);

  // Load more items when the user scrolls through the loadIncrement number of items
  useEffect(() => {
    // console.log("Items loaded: ", itemsLoaded.length, " page: ", page, " itemsToLoadPerPage: ", itemsToLoadPerPage);
    if (itemsLoaded.length >= itemsToLoadPerPage) {
      setItemsToLoadPerPage(itlpp => itlpp + (loadIncrement + (pageSize - loadIncrement)));
      // console.log("Load next page...");
      let newPage = page + 1;
      setPage(newPage);
      setLoadMore(true);
    }
  }, [itemsLoaded]);

  // Reset the API pagination and loaded data in the following scenarios:
  // - when the name filter changes
  // - when the participant filter changes
  // - when the assignee filter changes
  // - when the users refresh

  useEffect(() => {
    if (refreshUsers === true || (statusFilter !== previousValues?.statusFilter) || (participantFilter !== previousValues?.participantFilter && (participantFilter === "" || participantFilter?.length > 0)) || (assigneeFilter !== previousValues?.assigneeFilter && (assigneeFilter === "" || assigneeFilter?.length > 0))) {
      // console.log("Resetting...");
      // initialiseWorkflowUsers();
      setWorkflowUsers([]);
      setPage(1);
      setLastTaskId(null);
      setLastUserId(null);
      setLoadMore(true);
      setMoreItemsToLoad(true);
      setItemsToLoadPerPage(loadIncrement);
      setItemsLoaded([]);
    }
  }, [refreshUsers, statusFilter, participantFilter, assigneeFilter]);

  const onSuccessAssignUsersFormModal = () => {
    setShowAssignUsersFormModal(false);
    async function reload() {
      // await initialiseWorkflowUsers();
      setRefreshUsers(true);
      setRefreshTasks(true);
    }
    reload();
  };

  const unassignUser = async(e, doIt) => {
    setShowConfirmUnassign(false);
    if (doIt) {
      try {
        setIsLoading(true);
        let unassignApiResult = await API.delete({
          url: `/admin/${project?._id}/workflows/${workflowId}/users/${assignmentToDelete.user._id}`,
        });
        setRefreshUsers(true);
        setRefreshTasks(true);
      } catch(err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCSVDownload = async() => {
    try {
      setIsDownloadingData(true);
      let url = `/admin/${project?._id}/workflows/${workflowId}/reports/participantsData`;
      if (statusFilter) {
        url += `?statusMatch=${statusFilter}`;
      }
      if (participantFilter) {
        url += statusFilter ? `&participantsMatch=${participantFilter}` : `?participantsMatch=${participantFilter}`;
      }
      if (assigneeFilter) {
        url += statusFilter || participantFilter ? `&assigneesMatch=${assigneeFilter}` : `?assigneesMatch=${assigneeFilter}`;
      }
      let downloadApiResult = await API.get({
        url: url,
      });
      if (downloadApiResult.name === 'AxiosError') {
        throw new Error(downloadApiResult.message);
      }
      if (downloadApiResult?.data?.[0]) {
        const url = window.URL.createObjectURL(new Blob([downloadApiResult.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${downloadApiResult.project.name} - Workflow Users Report - ${moment().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
      } else {
        throw new Error('Error downloading data');
      }
    } catch(err) {
      console.error(err);
      setShowDownloadDataError(true);
    } finally {
      setIsDownloadingData(false);
    }
  };

  const onSuccessImportWorkflowUsersFormModal = () => {
    setShowImportWorkflowUsersFormModal(false);
    async function reload() {
      setRefreshUsers(true);
      setRefreshTasks(true);
      setRefreshWorkflow(true);
    }
    reload();
  };

  return <div>
    <div className="workflow-participants__filters">
      <div className="workflow-users-actions">
        { (project?.userPermissions?.isWorkflowManager || project?.userPermissions?.isCurrentUserOwner || project?.userPermissions?.isCurrentUserAdmin) && workflow?.status === 'Active' &&
        <>
          <Button
            style={{ marginLeft: '10px', height: '42px' }}
            onClick={() => {
              setShowAssignUsersFormModal(true);
            }}
            variant="outline-primary"
          >
            <FaPlus style={{ verticalAlign: "middle" }}></FaPlus>
            <span style={{ verticalAlign: "middle" }}> Assign User</span>
          </Button>
          <Button
            style={{ marginLeft: '10px', height: '42px' }}
            onClick={() => {
              setShowImportWorkflowUsersFormModal(true);
            }}
            variant="outline-primary"
          >
            <FaPlus style={{ verticalAlign: "middle" }}></FaPlus>
            <span style={{ verticalAlign: "middle" }}> Import Users</span>
          </Button>
        </>
        }
        <OverlayTrigger
          placement={'top'}
          overlay={
            <Tooltip id="delete-tooltip-disabled">
              <p>{!workflowUsers?.[0] ? 'There is no data to download with the current filters.' : ''}</p>
            </Tooltip>
          }
          show={!isLoading && !workflowUsers?.[0] && showDisableTooltip}
          onToggle={() => {
            !workflowUsers?.[0] ? setShowDisableTooltip(true) : setShowDisableTooltip(false);
          }}
        >
          <span
            className="d-inline-block"
            onMouseOver={() => setShowDisableTooltip(true)}
            onMouseLeave={() => setShowDisableTooltip(false)}>
            <Button
              onClick={() => {
                handleCSVDownload();
              }}
              variant="outline-primary"
              style={{ height: '42px', marginLeft: '10px', cursor: !workflowUsers?.[0] ? 'not-allowed' : 'pointer', pointerEvents: !workflowUsers?.[0] ? 'none' : 'auto' }}
              disabled={!workflowUsers?.[0]}
            >
              {isDownloadingData ? <CircularProgress color="inherit" size={20} /> : <FaDownload style={{ verticalAlign: "middle" }}></FaDownload>}
              <span style={{ verticalAlign: "middle" }}> Download Data</span>
            </Button>
          </span>
        </OverlayTrigger>
      </div>
      <div className='workflow-users-input-filters'>
        <div className='filter-wrapper'>
          <Form.Control
            style={{ height: '42px' }}
            as="select"
            placeholder="Status"
            onChange={(event) => {
              setStatusFilter(event.target.value);
            }}
            name="status"
            value={statusFilter}
          >
            <option value="AllComplete">All Complete</option>
            <option value="AllIncomplete">All Incomplete</option>
            <option value="OnTrack">-- On Track</option>
            <option value="TasksDue">-- Tasks Due</option>
          </Form.Control>
        </div>
        <div className='filter-wrapper'>
          <MultiSelectTypeahead
            name='participants'
            displayName='Participants'
            url={`/admin/${project._id}/workflows/${workflow._id}/usersLookup?assigned=0&pageSize=1000`}
            placeholder="Participants"
            defaultValue={[]}

            autocomplete="off"
            minSearchLength={minSearchLength}
            initApiSearchOnChange={true}
            mapResponse={(response) => {
              return response.map(u => {
                return {
                  name: u.displayName,
                  ...u
                };
              });
            }}

            multiple={true}
            allowNewItems={false}
            preventSelectAll={true}
            dynamicHeight={true}

            className={`form-control`}
            onChange={(value) => {
              setParticipantFilter(value.map(v => v._id).join(','));
            }}
            onBlur={() => {}}
            resetInputAfterSelectOrRemove={true}
          />
        </div>
        {(project?.userPermissions?.isWorkflowManager || project?.userPermissions?.isCurrentUserOwner || project?.userPermissions?.isCurrentUserAdmin) && (
          <div className='filter-wrapper' style={{ marginRight: '20px' }}>
            <MultiSelectTypeahead
              name='assignees'
              displayName='Assignees'
              url={`/admin/${project._id}/workflows/${workflow._id}/assignees`}
              placeholder="Assignees"
              defaultValue={[]}

              autocomplete="off"
              minSearchLength={minSearchLength}
              initApiSearchOnChange={true}
              mapResponse={(response) => {
                return response.map(u => {
                  return {
                    name: u.displayName,
                    ...u
                  };
                });
              }}

              multiple={true}
              allowNewItems={false}
              preventSelectAll={true}
              dynamicHeight={true}

              className={`form-control`}
              onChange={(value) => {
                setAssigneeFilter(value.map(v => v._id).join(','));
              }}
              onBlur={() => {}}
              resetInputAfterSelectOrRemove={true}
            />
          </div>
        )}
      </div>
    </div>

    <div className='workflow-users-list__refresh'>
      <Button
        onClick={() => {
          setRefreshUsers(true);
          setRefreshTasks(true);
        }}
        disabled={isLoading}
      >
        <LuRefreshCcw className={isLoading ? "refresh-users spin" : "refresh-users"} style={{ verticalAlign: "middle", marginTop: "-2px", marginRight: "10px" }}></LuRefreshCcw>
        <span style={{ verticalAlign: "middle" }}> Refresh</span>
      </Button>
    </div>

    {(workflowUsers?.[0]) ? (
      <div className="workflow-users-list">
        {workflowUsers.map(function(user, userIndex) {
          return <User
            key={userIndex}
            userIndex={userIndex}
            user={user}
            project={project}
            setAssignmentToView={setAssignmentToView}
            setShowConfirmView={setShowConfirmView}
            setAssignUserMode={setAssignUserMode}
            setEditUser={setEditUser}
            setShowAssignUsersFormModal={setShowAssignUsersFormModal}
            setAssignmentToDelete={setAssignmentToDelete}
            setShowConfirmUnassign={setShowConfirmUnassign}
            itemsLoaded={itemsLoaded}
            setItemsLoaded={setItemsLoaded}
          />;
        })}
      </div>
    ) : (!isLoading) ? (
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        No results
      </div>
    ) : (
      <></>
    )}
    {(isLoading) && (
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginBottom: '50px' }}>
        <Spinner animation="border" />
      </div>
    )}
    <ErrorModal
      title={`Error`}
      body={`Error downloading data`}
      show={showDownloadDataError}
      confirmtext="OK"
      onHide={() => setShowDownloadDataError(false)}
    />
    <ConfirmationModal
      title={`Unassign User`}
      body={`This will delete all associated data and can't be undone. Do you want to proceed?`}
      show={showConfirmUnassign}
      confirmtext="OK"
      onHide={unassignUser}
    />
    <AssignWorkflowUsersFormModal
      workflow={workflow}
      mode={assignUserMode}
      user={editUser}
      show={showAssignUsersFormModal}
      onCancel={() => {
        setShowAssignUsersFormModal(false);
        setAssignUserMode('create');
        setEditUser(null);
      }}
      onSuccess={() => {
        onSuccessAssignUsersFormModal();
        setAssignUserMode('create');
        setEditUser(null);
      }}
    />
    <ViewAssignedUsersTasksModal
      workflow={workflow}
      user={assignmentToView}
      show={showConfirmView}
      onCancel={() => {
        setShowConfirmView(false);
        setAssignmentToView(null);
        setRefreshUsers(true);
        setRefreshTasks(true);
      }}
      onSuccess={() => {
        setShowConfirmView(false);
        setAssignmentToView(null);
        setRefreshUsers(true);
        setRefreshTasks(true);
      }}
    />
    <ImportWorkflowUsersFormModal
      workflow={workflow}
      show={showImportWorkflowUsersFormModal}
      onCancel={() => {
        setShowImportWorkflowUsersFormModal(false);
      }}
      onSuccess={() => {
        onSuccessImportWorkflowUsersFormModal();
      }}
    />
  </div>;
}